import { useEffect, useRef, useState } from "react";

/**
 * Animated counter that starts when the element enters the viewport.
 * Accepts strings like "12+", "100%", "30+" or pure numbers.
 * Parses the leading integer, animates from 0 → target, and keeps the suffix.
 */
export function CountUp({ value, duration = 1800, className = "" }) {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    const raw = String(value ?? "");
    const match = raw.match(/^(\d+)(.*)$/);
    if (!match) {
      setDisplay(raw);
      return;
    }
    const target = parseInt(match[1], 10);
    const suffix = match[2] || "";

    setDisplay(`0${suffix}`);

    const startAnimation = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const startTime = performance.now();

      const step = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = Math.round(target * eased);
        setDisplay(`${current}${suffix}`);
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const el = ref.current;
    if (!el) return;

    if (!("IntersectionObserver" in window)) {
      startAnimation();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startAnimation();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}
